import { FLabel } from '@components/form/FLabel';
import { MultiComboBoxInput } from '@components/inputs/ComboBox';
import { type DropdownOptions } from '@components/inputs/SelectInput/MultiSelectInput/MultiSelectInput';
import { LoadingSpinner } from '@components/loadingSpinner';
import {
  useFetchAssignableUsers,
  useUpdateAssignment,
  useUpdateStatus,
} from 'api/anomalies/anomalyServices';
import { useFeatureFlag } from 'context/FeatureFlagProvider/FeatureFlagProvider';
import { useDebounce } from 'hooks/useDebounce';
import { type AnomalyAssignedToUser } from 'interfaces/change/ChangeData.interface';
import { CardContainer } from 'layout/Card';
import { FEATURE_FLAGS_LIST } from 'lib/featureFlagsList';
import { SelectStatus } from 'pages/Anomalies/AnomalyListPage/components/SelectStatus';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { getDaysFromDate } from 'utils/formatters/getDaysFromDate/getDaysFromDate';

interface StatusProps {
  currentUser?: AnomalyAssignedToUser;
  currentStatus?: string;
  timeOnStatus?: string;
  changeId?: string;
  statusList?: string[];
  isLoading?: boolean;
}

const Status: FC<StatusProps> = ({
  changeId,
  timeOnStatus,
  currentStatus,
  currentUser,
  statusList,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { mutate: updateStatus, isPending: isUpdateStatusLoading } =
    useUpdateStatus(changeId);
  const { mutate: updateAssignment, isPending: isUpdateAssignmentLoading } =
    useUpdateAssignment(changeId);
  const [statusValue, setStatusValue] = useState<string | number | undefined>(
    currentStatus
  );

  const [userResponsible, setUserResponsible] = useState<DropdownOptions>();

  const [queryUser, setQueryUser] = useState('');
  const queryDebounced = useDebounce(queryUser, 300);

  const { hasFeatureFlagPermission } = useFeatureFlag();

  const { data, isFetching } = useFetchAssignableUsers(queryUser, changeId);

  const options = useMemo(() => {
    if (!data?.users?.length) return [];
    return data.users.map((user) => {
      const fullName = `${user.given_name} ${user.family_name}`;

      return {
        name: fullName,
        value: user.id,
      };
    });
  }, [data]);

  useEffect(() => {
    if (!currentUser || !data?.users?.length) {
      // Reset to undefined if no current user
      setUserResponsible(undefined);
      return;
    }

    const fullName = `${String(currentUser.given_name ?? '')} ${String(
      currentUser.family_name ?? ''
    )}`;
    const option = { value: currentUser.id, name: fullName };

    // Only update if the user has changed
    if (!userResponsible || userResponsible.value !== currentUser.id) {
      setUserResponsible(option);
    }
  }, [currentUser, data, userResponsible]);

  useEffect(() => {
    if (currentStatus) setStatusValue(currentStatus);
  }, [currentStatus]);

  const handleStatusChange = (value: string | number | undefined): void => {
    setStatusValue(value);
    if (value && value !== currentStatus) {
      updateStatus({ status: value });
    }
  };

  const handleUserResponsibleChange = (
    value: DropdownOptions | DropdownOptions[]
  ): void => {
    if (!Array.isArray(value)) {
      setUserResponsible(value);
      const assignedUserId = value?.value ? Number(value.value) : undefined;
      const currentUserId = currentUser?.id;

      if (assignedUserId !== currentUserId) {
        updateAssignment({ assigned_to_user: assignedUserId });
      }
    }
  };

  return (
    <CardContainer>
      <div className="flex justify-start gap-2 items-center mb-4">
        {isLoading ? (
          <Skeleton containerClassName="flex-1 max-w-[250px]" height={'20px'} />
        ) : (
          <>
            <label
              style={{ fontSize: 14 }}
              className="font-bold uppercase text-gray-60"
            >
              {t('common.responsibilityAndStatus')}
            </label>
          </>
        )}
      </div>
      <div className="flex justify-between center gap-6">
        {isLoading ? (
          <Skeleton count={2} containerClassName="flex-1" height={'30px'} />
        ) : (
          <div className="flex flex-col gap-2 w-full">
            <FLabel> {t('common.responsible')}</FLabel>
            <div className="relative">
              <MultiComboBoxInput
                showSearchIcon={false}
                multiple={false}
                isFetching={isFetching || isUpdateAssignmentLoading}
                options={options ?? []}
                placeholder={t('common.placeholders.selectUser')}
                onChange={handleUserResponsibleChange}
                query={queryDebounced}
                handleQuery={setQueryUser}
                initialValues={userResponsible}
                data-testid="user-select"
                disableSelectedList={true}
                disabled={
                  !hasFeatureFlagPermission(
                    FEATURE_FLAGS_LIST.changes.patch_status
                  ) || isUpdateAssignmentLoading
                }
              />

              {isUpdateAssignmentLoading && (
                <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <LoadingSpinner size={16} />
                </div>
              )}
            </div>
            <p className="text-xs text-gray-50/75 line-height-[140%] mt-1">
              {t('anomaliesPage.status.responsibleEmailNotification')}
            </p>
          </div>
        )}

        {isLoading ? (
          <Skeleton count={2} containerClassName="flex-1" height={'30px'} />
        ) : (
          <div className="flex flex-col gap-2 w-full">
            <div className="flex justify-between items-center">
              <FLabel>{t('common.statusLabel')}</FLabel>

              <span>
                {timeOnStatus &&
                  t('anomaliesPage.sideMenu.timeOnStatus', {
                    time: getDaysFromDate(timeOnStatus),
                  })}
              </span>
            </div>

            <div className="relative">
              <SelectStatus
                value={statusValue}
                statusList={statusList}
                onChange={handleStatusChange}
                disabled={
                  !hasFeatureFlagPermission(
                    FEATURE_FLAGS_LIST.changes.patch_status
                  ) || isUpdateStatusLoading
                }
                isLoading={isUpdateStatusLoading}
              />

              {isUpdateStatusLoading && (
                <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <LoadingSpinner size={16} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CardContainer>
  );
};
export default Status;
